// The prompts that wait, above the composer (Poulpe-430): a person's prompt
// sent while the session's turn runs waits there until the turn ends. Each
// row says where it stands (Next, or Follow-up), its first line and its
// files, and can be sent now, which hands it to the turn that runs and stops
// nothing; moved up, ahead of the follow-ups, or down again; or unsent, which
// takes it back into the box, with its files, to edit. One that has been sent
// to the agent is no longer listed, and cannot be unsent. Today's words
// (code/Composer.tsx there), on the new server's `change_queued_prompt`.
import { useState } from 'react'
import { errorMessage } from '../api/client'
import type { ChangeQueuedPromptArgs, QueuedPrompt, Session, UnsentPrompt, WireAttachment } from '../api/types'
import { uuid4 } from '../api/uuid'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useBoardState, useStore } from '../store/context'

/** The first line of a prompt, cut at 140 characters. */
function firstLine(text: string): string {
  const line = text.trim().split('\n', 1)[0] ?? ''
  return line.length > 140 ? `${line.slice(0, 140)}…` : line
}

/** The files a prompt that was taken back carried, as files again. */
export function filesOf(attachments: readonly WireAttachment[]): File[] {
  return attachments.map((file) => {
    const bytes = Uint8Array.from(atob(file.data), (char) => char.charCodeAt(0))
    return new File([bytes], file.name, { type: file.mime_type })
  })
}

/** What the box holds once a prompt is taken back into it: the prompt, then what he had begun to write. */
export function withUnsent(draft: string, unsent: UnsentPrompt): string {
  return draft.trim() === '' ? unsent.prompt : `${unsent.prompt}\n\n${draft}`
}

const label = (entry: QueuedPrompt) => (entry.priority === 'next' ? 'Next' : 'Follow-up')

export function PromptQueue({ session, onUnsent }: { session: Session; onUnsent: (unsent: UnsentPrompt) => void }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  // The prompts whose request is out: their buttons wait for the answer.
  const [asked, setAsked] = useState<readonly string[]>([])
  const queue = session.queued ?? []
  if (queue.length === 0) return null

  const ask = (entry: QueuedPrompt, change: ChangeQueuedPromptArgs['change'], failed: string) => {
    if (asked.includes(entry.id) || store.getState().offline) return
    setAsked((held) => [...held, entry.id])
    store
      .changeQueuedPrompt({ mutation_id: uuid4(), op: 'change_queued_prompt', args: { session_id: session.id, prompt_id: entry.id, change } })
      .then(({ unsent }) => {
        if (unsent) onUnsent(unsent)
      })
      .catch((err: unknown) => toast(`${failed}: ${errorMessage(err)}`, 'error'))
      .finally(() => {
        if (mounted.current) setAsked((held) => held.filter((id) => id !== entry.id))
      })
  }
  return (
    <ul className="code-queue" aria-label="Prompts that wait">
      {queue.map((entry) => {
        const busy = asked.includes(entry.id) || offline
        return (
          <li key={entry.id} title={entry.prompt}>
            <span className="tag">{label(entry)}</span>
            <span className="code-queue-text">{firstLine(entry.prompt)}</span>
            {entry.files.length > 0 && <span className="code-queue-files">{entry.files.length === 1 ? '1 file' : `${entry.files.length} files`}</span>}
            <span className="code-queue-actions">
              <button
                type="button"
                className="button button-quiet code-queue-now"
                disabled={busy}
                aria-label="Send now: the turn that runs takes this prompt in at its next step, and nothing is stopped"
                title="Hand this prompt to the turn that runs: it takes it in at its next step, and nothing is stopped"
                {...needsServer(offline)}
                onClick={() => ask(entry, 'send_now', 'Could not send the prompt now')}
              >
                Send now
              </button>
              <button
                type="button"
                className="button button-quiet code-queue-move"
                disabled={busy}
                aria-label={entry.priority === 'next' ? 'Make follow-up: this prompt goes after the others that wait' : 'Make next: this prompt goes ahead of the follow-ups'}
                title={entry.priority === 'next' ? 'Make it a follow-up: it goes after the others that wait' : 'Make it next: it goes ahead of the follow-ups'}
                {...needsServer(offline)}
                onClick={() => ask(entry, entry.priority === 'next' ? 'later' : 'next', 'Could not move the prompt')}
              >
                {entry.priority === 'next' ? 'Make follow-up' : 'Make next'}
              </button>
              {/* A word, not a cross: nothing is deleted, the prompt comes back to be edited. */}
              <button
                type="button"
                className="button button-quiet code-queue-unsend"
                disabled={busy}
                aria-label="Unsend: take this prompt back into the box to edit"
                title="Unsend: take this prompt back into the box to edit"
                {...needsServer(offline)}
                onClick={() => ask(entry, 'unsend', 'Could not unsend the prompt')}
              >
                Unsend
              </button>
            </span>
          </li>
        )
      })}
    </ul>
  )
}
